import * as AppConstants from 'app/AppConstants';
import keyBy from 'lodash/keyBy';
import {insecureHash} from 'utilities/hash';
import {concat, difference} from 'utilities/Set';
import {DEFAULT_BY_COLUMN_TYPE, mapDataType} from 'constants/FilterConstants';
import constantObject from 'utilities/constantObject';

export const STATES = constantObject(['LOADING', 'LOADED', 'ERROR']);

const initialState = () => ({
  filters: {},
  filtersById: {},
  dataSourcesById: {},
  filtersStateById: {},
  hasQueryStringErrorById: {},
});

const isCompleteFilter = filter =>
  filter.column && filter.dataType && filter.operand && filter.values;

const defaultOperand = dataType =>
  DEFAULT_BY_COLUMN_TYPE[mapDataType(dataType)].value;

const keyedFilter = (stackId, filter) => ({
  ...filter,
  id: insecureHash(
    `${stackId}${filter.dataSourceId}${filter.column}${filter.dataType}`
  ),
});

const removeFilters = (state, stackId, filterIds) => {
  const filters = {...state.filters};
  filterIds.forEach(filterId => delete filters[filterId]);
  const filtersById = difference(state.filtersById[stackId], filterIds);

  return {
    ...state,
    filters,
    filtersById: {
      ...state.filtersById,
      [stackId]: filtersById,
    },
  };
};

export default (state = initialState(), action) => {
  switch (action.type) {
    case AppConstants.CARD__LOAD_QUERYSTRING_PAGE_FILTERS_STARTED:
    case AppConstants.CARD__LOAD_PAGE_FILTERS_STARTED: {
      return {
        ...state,
        filtersStateById: {
          [action.payload.id]: STATES.LOADING,
        },
      };
    }

    case AppConstants.CARD__LOAD_QUERYSTRING_PAGE_FILTERS_FINISHED:
    case AppConstants.CARD__LOAD_PAGE_FILTERS_FINISHED: {
      const {dataSources, filters} = action.payload;
      const appliedFilters = filters.map(f => ({
        applied: isCompleteFilter(f),
        values: [],
        operand: defaultOperand(f.dataType),
        persisted: true,
        ...f,
      }));
      const stackId = action.payload.id;
      const dataSourceIds = new Set(dataSources.map(d => d.id));
      const validFilters = appliedFilters.map(f => keyedFilter(stackId, f));
      const keyedFilters = keyBy(validFilters, 'id');
      const filterIds = new Set(Object.keys(keyedFilters));

      return {
        ...state,
        filters: {
          ...state.filters,
          ...keyedFilters,
        },
        filtersById: {
          ...state.filtersById,
          [stackId]: filterIds,
        },
        dataSourcesById: {
          ...state.dataSourcesById,
          [stackId]: dataSourceIds,
        },
        filtersStateById: {
          ...state.filtersStateById,
          [stackId]: STATES.LOADED,
        },
      };
    }

    case AppConstants.CARD__LOAD_QUERYSTRING_PAGE_FILTERS_FAILED: {
      return {
        ...state,
        hasQueryStringErrorById: {
          [action.payload.id]: true,
        },
        filtersStateById: {
          ...state.filtersStateById,
          [action.payload.id]: STATES.ERROR,
        },
      };
    }

    case AppConstants.STACK__CLEARED_QUERYSTRING_ERROR: {
      return {
        ...state,
        hasQueryStringErrorById: {
          ...state.hasQueryStringErrorById,
          [action.payload.stackId]: false,
        },
      };
    }
    case AppConstants.CARD__LOAD_PAGE_FILTERS_FAILED: {
      return {
        ...state,
        filtersStateById: {
          ...state.filtersStateById,
          [action.payload.id]: STATES.ERROR,
        },
      };
    }

    case AppConstants.STACK__ADDED_PAGE_FILTER: {
      const {stackId, filter} = action.payload;

      return {
        ...state,
        filters: {
          ...state.filters,
          [filter.id]: filter,
        },
        filtersById: {
          ...state.filtersById,
          [stackId]: concat([filter.id], state.filtersById[stackId]),
        },
      };
    }

    case AppConstants.STACK__REMOVED_PAGE_FILTER: {
      const {filterId, stackId} = action.payload;

      return removeFilters(state, stackId, [filterId]);
    }
    case AppConstants.STACK__APPLIED_PAGE_FILTER: {
      const {filter} = action.payload;

      return {
        ...state,
        filters: {
          ...state.filters,
          [filter.id]: {
            ...filter,
            applied: true,
          },
        },
      };
    }

    case AppConstants.STACK__PAGE_FILTERS_SAVED: {
      const {filters} = action.payload;
      const persistedFilters = filters.map(f => ({...f, persisted: true}));
      const keyedFilters = keyBy(persistedFilters, 'id');

      return {
        ...state,
        filters: {
          ...state.filters,
          ...keyedFilters,
        },
      };
    }

    default:
      return state;
  }
};
